import { useEffect, useState } from "react";
import { Check, ChevronDown, Loader2, Save, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSoloModeExperienceState } from "@/hooks/useSoloModeExperienceState";
import { useUpdateSettings, useUserSettings } from "@/hooks/useUserSettings";
import {
  DEFAULT_LEADERSHIP_LENS_ID,
  LEADERSHIP_LENS_OPTIONS,
  resolveLeadershipLens,
  type LeadershipLensId,
} from "@/lib/leadershipLens";
import { cn } from "@/lib/utils";

interface LeadershipFrameworkPickerContentProps {
  selectedLens: LeadershipLensId;
  savedLens: LeadershipLensId;
  onSelectedLensChange: (lens: LeadershipLensId) => void;
  onSave: () => void | Promise<void>;
  isSaving?: boolean;
  saveDisabled?: boolean;
}

function LeadershipFrameworkSummary({ lens }: { lens: LeadershipLensId }) {
  const selectedLeadershipFramework = resolveLeadershipLens(lens);

  return (
    <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
        Current framework
      </p>
      <p className="mt-1 text-small font-semibold text-foreground">
        {selectedLeadershipFramework.label}
      </p>
      <p className="mt-1 text-caption text-muted-foreground">
        {selectedLeadershipFramework.description}
      </p>
    </div>
  );
}

function LeadershipFrameworkPickerContent({
  selectedLens,
  savedLens,
  onSelectedLensChange,
  onSave,
  isSaving = false,
  saveDisabled = false,
}: LeadershipFrameworkPickerContentProps) {
  return (
    <div className="space-y-4">
      <LeadershipFrameworkSummary lens={savedLens} />

      <div className="grid gap-3" aria-label="Leadership framework options">
        {LEADERSHIP_LENS_OPTIONS.map((option) => {
          const isSelected = option.id === selectedLens;

          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={isSelected}
              onClick={() => onSelectedLensChange(option.id)}
              className={cn(
                "w-full rounded-xl border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                isSelected
                  ? "border-primary bg-primary/10"
                  : "border-border bg-card hover:border-primary/50 hover:bg-muted/30",
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <p className="text-small font-semibold text-foreground">{option.label}</p>
                  <p className="text-caption text-muted-foreground">{option.description}</p>
                </div>
                <span
                  className={cn(
                    "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                    isSelected ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40",
                  )}
                  aria-hidden="true"
                >
                  {isSelected ? <Check className="h-3.5 w-3.5" /> : null}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-caption text-muted-foreground">
        Changing frameworks changes the reflection prompts and language you see going forward. It does not label you, compare you, or judge your growth.
      </p>
      <div className="flex flex-col gap-2 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-caption text-muted-foreground">
          Save when this is the framework you want Coach Veera to use next.
        </p>
        <Button
          type="button"
          size="sm"
          onClick={() => void onSave()}
          disabled={saveDisabled || isSaving}
        >
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Framework
        </Button>
      </div>
    </div>
  );
}

interface LeadershipFrameworkPickerCardProps extends LeadershipFrameworkPickerContentProps {
  id?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LeadershipFrameworkPickerCard({
  id,
  isOpen,
  onOpenChange,
  selectedLens,
  savedLens,
  onSelectedLensChange,
  onSave,
  isSaving,
  saveDisabled,
}: LeadershipFrameworkPickerCardProps) {
  return (
    <Card id={id} className="animate-slide-up">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2 text-body">
              <Sparkles className="h-5 w-5 text-primary" />
              Leadership Frameworks
            </CardTitle>
            <p className="mt-1 text-caption text-muted-foreground">
              Choose the framework Coach Veera should use to shape reflection prompts and language. This is a preference for focus, not a judgment.
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="shrink-0"
            onClick={() => onOpenChange(!isOpen)}
            aria-controls="leadership-frameworks-content"
            aria-expanded={isOpen}
            aria-label={`${isOpen ? "Hide" : "Edit"} Leadership Frameworks`}
          >
            {isOpen ? "Hide" : "Edit"}
            <ChevronDown
              className={cn(
                "ml-1 h-4 w-4 transition-transform",
                isOpen ? "rotate-180" : "",
              )}
            />
          </Button>
        </div>
      </CardHeader>
      {!isOpen ? (
        <CardContent className="pt-0">
          <LeadershipFrameworkSummary lens={savedLens} />
        </CardContent>
      ) : (
        <CardContent id="leadership-frameworks-content">
          <LeadershipFrameworkPickerContent
            selectedLens={selectedLens}
            savedLens={savedLens}
            onSelectedLensChange={onSelectedLensChange}
            onSave={onSave}
            isSaving={isSaving}
            saveDisabled={saveDisabled}
          />
        </CardContent>
      )}
    </Card>
  );
}

export function LeadershipFrameworkPickerDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: settings } = useUserSettings();
  const soloModeExperience = useSoloModeExperienceState();
  const updateMutation = useUpdateSettings();
  const savedLens = resolveLeadershipLens(
    soloModeExperience.leadershipLens || settings?.leadershipLens || DEFAULT_LEADERSHIP_LENS_ID,
  ).id;
  const [selectedLens, setSelectedLens] = useState<LeadershipLensId>(savedLens);

  useEffect(() => {
    if (open) {
      setSelectedLens(savedLens);
    }
  }, [open, savedLens]);

  const handleSave = async () => {
    await updateMutation.mutateAsync({ leadershipLens: selectedLens });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Leadership Frameworks
          </DialogTitle>
          <DialogDescription>
            Choose the framework Coach Veera should use to shape reflection prompts and language. You can also change this from Settings.
          </DialogDescription>
        </DialogHeader>
        <LeadershipFrameworkPickerContent
          selectedLens={selectedLens}
          savedLens={savedLens}
          onSelectedLensChange={setSelectedLens}
          onSave={handleSave}
          isSaving={updateMutation.isPending}
          saveDisabled={selectedLens === savedLens}
        />
      </DialogContent>
    </Dialog>
  );
}
